import { useState } from "react";
import { Copy, Check, Download, Send } from "lucide-react";
import {
  buildMarkdown,
  copyMarkdown,
  copyForSlack,
  downloadMarkdown,
  type ExportPayload,
} from "@/lib/export";

/**
 * ExportBar - share affordances for a completed brief.
 *   • Copy as Markdown
 *   • Download .md
 *   • Copy for Slack
 *
 * No heavy deps. Disabled until there is something to export.
 */

interface ExportBarProps {
  payload: ExportPayload;
  /** Compact = icon-only buttons (used in tight headers). */
  compact?: boolean;
  className?: string;
}

export function ExportBar({ payload, compact = false, className = "" }: ExportBarProps) {
  const [copied, setCopied] = useState<"md" | "slack" | null>(null);

  const hasContent =
    Boolean(payload.threat) ||
    Boolean(payload.synthesisText && payload.synthesisText.trim()) ||
    Boolean(payload.battlecard && payload.battlecard.markdown.trim());

  // buildMarkdown is cheap and lets us disable when truly empty.
  const empty = !hasContent || buildMarkdown(payload).trim().length < 40;

  async function onCopyMd() {
    if (await copyMarkdown(payload)) {
      setCopied("md");
      setTimeout(() => setCopied(null), 1500);
    }
  }
  async function onCopySlack() {
    if (await copyForSlack(payload)) {
      setCopied("slack");
      setTimeout(() => setCopied(null), 1500);
    }
  }

  const btn =
    "inline-flex items-center gap-1.5 rounded-lg border border-[#1a2640] bg-white/[0.03] text-slate-400 hover:text-[#22d3ee] hover:border-[#22d3ee]/30 hover:bg-[#22d3ee]/5 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed " +
    (compact ? "px-2.5 py-2 min-h-[40px]" : "px-3 py-2 text-xs min-h-[40px]");

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      data-print="hide"
      role="group"
      aria-label="Export and share"
    >
      <button type="button" onClick={onCopyMd} disabled={empty} className={btn} title="Copy as Markdown">
        {copied === "md" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        {!compact && <span>{copied === "md" ? "Copied" : "Copy Markdown"}</span>}
      </button>

      <button
        type="button"
        onClick={() => downloadMarkdown(payload)}
        disabled={empty}
        className={btn}
        title="Download as .md file"
      >
        <Download className="w-3.5 h-3.5" />
        {!compact && <span>Download .md</span>}
      </button>

      <button type="button" onClick={onCopySlack} disabled={empty} className={btn} title="Copy for Slack">
        {copied === "slack" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Send className="w-3.5 h-3.5" />}
        {!compact && <span>{copied === "slack" ? "Copied" : "Copy for Slack"}</span>}
      </button>
    </div>
  );
}
